import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { clientsAPI, messagesAPI, archiveAPI, exportAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2,
  FolderOpen,
  FileText,
  Mail,
  Phone,
  MapPin,
  User,
  Send,
  Clock,
  CheckCircle,
  Download,
  Archive,
  ClipboardList,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import { formatDate, formatDateTime, formatRelativeTime, getStatusLabel, getClientStatusLabel } from '../../lib/utils';

const ClientProfilePage = () => {
  const { id } = useParams();
  const { user, isStaff } = useAuth();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('apercu');
  const [exporting, setExporting] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, projectsRes, filesRes, reqDocsRes, messagesRes] = await Promise.all([
        clientsAPI.getProfile(id),
        clientsAPI.getProjects(id),
        clientsAPI.getAllFiles(id),
        clientsAPI.getRequiredDocs(id),
        messagesAPI.getByClient(id),
      ]);
      setClient(profileRes.data);
      setProjects(projectsRes.data);
      setFiles(filesRes.data);
      setRequiredDocs(reqDocsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du profil client');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await messagesAPI.send({
        client_id: id,
        contenu: newMessage,
      });
      toast.success('Message envoyé');
      setNewMessage('');
      // Refresh messages
      const messagesRes = await messagesAPI.getByClient(id);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleExportProject = async (projectId, projectName) => {
    setExporting(projectId);
    try {
      const url = exportAPI.getProjectZipUrl(projectId);
      window.open(url, '_blank');
      toast.success(`Export du projet "${projectName}" lancé`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(null);
    }
  };

  const handleArchiveProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver ce projet?')) return;
    try {
      await archiveAPI.archiveProject(projectId);
      toast.success('Projet archivé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approuve':
        return 'bg-green-100 text-green-800';
      case 'refuse':
      case 'rejete':
        return 'bg-red-100 text-red-800';
      case 'en_cours_revision':
        return 'bg-blue-100 text-blue-800';
      case 'recu':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600">Client non trouvé.</p>
        <Link to="/portail/clients">
          <Button className="mt-4">Retour aux clients</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/portail/clients">Clients</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{client.nom_entreprise}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Client Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-2xl font-bold text-slate-900">
                  {client.nom_entreprise}
                </h1>
                <Badge className={client.statut === 'actif' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>
                  {getClientStatusLabel(client.statut || 'actif')}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
                {client.personne_contact && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {client.personne_contact}
                  </div>
                )}
                {client.email_contact && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {client.email_contact}
                  </div>
                )}
                {client.telephone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {client.telephone}
                  </div>
                )}
                {client.adresse && (
                  <div className="flex items-center gap-2 md:col-span-3">
                    <MapPin className="w-4 h-4" />
                    {client.adresse}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-primary">{client.projets_count}</p>
                <p className="text-xs text-slate-500">Projets</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-primary">{client.fichiers_count}</p>
                <p className="text-xs text-slate-500">Fichiers</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="apercu" data-testid="tab-apercu">
            <FolderOpen className="w-4 h-4 mr-2" />
            Projets
          </TabsTrigger>
          <TabsTrigger value="fichiers" data-testid="tab-fichiers">
            <FileText className="w-4 h-4 mr-2" />
            Fichiers
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <ClipboardList className="w-4 h-4 mr-2" />
            Requis
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">
            <MessageSquare className="w-4 h-4 mr-2" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-historique">
            <Clock className="w-4 h-4 mr-2" />
            Activité
          </TabsTrigger>
        </TabsList>

        {/* Projects Tab */}
        <TabsContent value="apercu" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Projets ({projects.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {projects.length === 0 ? (
                <div className="py-12 text-center">
                  <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucun projet pour ce client.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Projet</TableHead>
                      <TableHead className="hidden md:table-cell">Statut</TableHead>
                      <TableHead className="hidden md:table-cell">Fichiers</TableHead>
                      <TableHead className="hidden lg:table-cell">Dernière activité</TableHead>
                      <TableHead className="w-40">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <Link
                            to={`/portail/projets/${project.id}`}
                            className="font-medium text-primary hover:underline"
                          >
                            {project.nom}
                          </Link>
                          {project.description && (
                            <p className="text-xs text-slate-500 line-clamp-1">{project.description}</p>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant={project.statut === 'actif' ? 'default' : 'secondary'}>
                            {getStatusLabel(project.statut)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {project.fichiers_count || 0}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                          {formatRelativeTime(project.derniere_activite)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportProject(project.id, project.nom)}
                              disabled={exporting === project.id}
                              title="Exporter ZIP"
                            >
                              {exporting === project.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                            {isStaff && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchiveProject(project.id)}
                                className="text-amber-600 hover:text-amber-700"
                                title="Archiver"
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="fichiers" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Tous les fichiers ({files.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {files.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucun fichier.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fichier</TableHead>
                      <TableHead className="hidden md:table-cell">Projet</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="hidden md:table-cell">Date</TableHead>
                      <TableHead className="w-20">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {files.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{file.nom_fichier_original}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-slate-500">
                          {file.project_nom}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(file.statut)} variant="secondary">
                            {getStatusLabel(file.statut)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-slate-500">
                          {formatDate(file.created_at)}
                        </TableCell>
                        <TableCell>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Required Docs Tab */}
        <TabsContent value="documents" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Documents requis ({requiredDocs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {requiredDocs.length === 0 ? (
                <div className="py-12 text-center">
                  <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucun document requis.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {requiredDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        doc.statut === 'recu' ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {doc.statut === 'recu' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{doc.titre}</p>
                        <p className="text-sm text-slate-500">{doc.project_nom}</p>
                      </div>
                      <Badge className={doc.statut === 'recu' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                        {doc.statut === 'recu' ? 'Reçu' : 'En attente'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Messagerie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Message Input */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Écrivez votre message..."
                  rows={3}
                  className="mb-3"
                  data-testid="message-input"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Envoyer
                  </Button>
                </div>
              </div>

              {/* Messages List */}
              {messages.length === 0 ? (
                <div className="py-8 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun message. Commencez la conversation!</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[500px] overflow-y-auto">
                  {messages.map((msg) => {
                    const isOwnMessage = msg.sender_user_id === user?.id;
                    const isStaffMessage = msg.sender_role !== 'client';
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] p-4 rounded-lg ${
                            isOwnMessage
                              ? 'bg-primary text-white'
                              : isStaffMessage
                              ? 'bg-blue-50 border border-blue-100'
                              : 'bg-slate-100'
                          }`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-medium ${isOwnMessage ? 'text-white/90' : 'text-slate-700'}`}>
                              {msg.sender_nom}
                            </span>
                            {isStaffMessage && !isOwnMessage && (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                PEY
                              </Badge>
                            )}
                          </div>
                          <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-slate-600'}`}>
                            {msg.contenu}
                          </p>
                          {msg.project_nom && (
                            <p className={`text-xs mt-2 ${isOwnMessage ? 'text-white/70' : 'text-slate-400'}`}>
                              Re: {msg.project_nom}
                            </p>
                          )}
                          <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-slate-400'}`}>
                            {formatRelativeTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="historique" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Historique des activités</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>L'historique complet des activités sera disponible bientôt.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientProfilePage;
